import type { SaveData } from '../persistence/save.js';

/**
 * The Discovery log (playtest request).
 *
 * What a run OBSERVED, gathered while it plays and folded into the permanent save when
 * it ends. Kept as a plain value with pure functions over it so the merge rules — which
 * are where a compendium quietly loses data — can be tested without a browser or a world.
 */
export interface RunDiscovery {
  /** Every tower defId placed this run, including ones later sold or merged away. */
  towers: Set<string>;
  /** Enemy archetypes seen at all, whether or not any died. */
  enemiesSeen: Set<string>;
  /** Kills this run, by archetype. */
  kills: Map<string, number>;
}

export function emptyRunDiscovery(): RunDiscovery {
  return { towers: new Set(), enemiesSeen: new Set(), kills: new Map() };
}

/**
 * Fold a run's observations into the permanent log.
 *
 * Returns a NEW discovery object rather than mutating, so it composes with the save's
 * copy-on-write update. Kills add; a "seen" enemy that has never died enters at zero and
 * must not overwrite a real count from an earlier run — which is the one way a naive
 * merge loses data, and why `seen` is applied before the additions rather than after.
 */
export function mergeDiscovery(
  previous: SaveData['discovery'],
  run: RunDiscovery,
): SaveData['discovery'] {
  const towers = new Set(previous.towers);
  for (const id of run.towers) towers.add(id);

  const enemyKills: Record<string, number> = { ...previous.enemyKills };
  for (const id of run.enemiesSeen) enemyKills[id] ??= 0;
  for (const [id, n] of run.kills) enemyKills[id] = (enemyKills[id] ?? 0) + n;

  return {
    // Sorted so the stored order does not depend on the order things happened to be
    // encountered in — a save that reshuffles on every write is noise in a diff.
    towers: [...towers].sort(),
    enemyKills,
  };
}

/** Has this been met at all? Presence, not count: zero kills is still discovered. */
export function isDiscovered(discovery: SaveData['discovery'], id: string): boolean {
  return discovery.towers.includes(id) || id in discovery.enemyKills;
}
